// Shared page-level primitives. The controls (Button, Field, Notice, Avatar, logo) are thin
// adapters over @grackle/ui (SD35) that keep the props the pages already pass (`loading`,
// `tone="error"`, `email` for the avatar seed), so page logic is untouched and there is one
// visual system underneath.
import { forwardRef, type ReactNode } from "react";
import {
  Avatar as DsAvatar,
  Badge,
  Button as DsButton,
  Callout,
  EmptyState as DsEmptyState,
  Icon,
  Input,
  Wordmark,
  type ButtonProps as DsButtonProps,
  type InputProps,
} from "@grackle/ui";

export const Field = forwardRef<HTMLInputElement, InputProps & { label: string }>(function Field(props, ref) {
  return <Input ref={ref} {...props} />;
});

export function initials(name?: string, email?: string): string {
  const source = (name || email || "G").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return ((parts[0][0] ?? "") + (parts[1][0] ?? "")).toUpperCase();
  return source.slice(0, 2).toUpperCase() || "G";
}

export function Avatar({ name, email, size = 26 }: { name?: string; email?: string; size?: number }) {
  return <DsAvatar name={name || email || ""} initials={initials(name, email)} size={size} speaker={1} aria-hidden />;
}

export function PageLoader({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="center-page" role="status" aria-live="polite">
      <Icon name="circle-notch" size={20} className="spin" />
      <span>{label}</span>
    </div>
  );
}

/** The DS EmptyState (dashed panel, purple icon disc, one action) under the props the pages
 * already pass: `body` is the DS `description`. One action, never two. */
export function EmptyState({
  icon = "video-camera",
  title,
  body,
  action,
  className,
}: {
  icon?: string;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return <DsEmptyState icon={icon} title={title} description={body} action={action} className={className} />;
}

interface ButtonProps extends DsButtonProps {
  loading?: boolean;
}

/** DS Button that is disabled and aria-busy while `loading`. */
export function Button({ loading, disabled, ...props }: ButtonProps) {
  return <DsButton disabled={disabled || loading} aria-busy={loading} {...props} />;
}

export function GrackleLogo() {
  return <Wordmark size={20} />;
}

/** F13b: "On legal hold" chip shown to a recording's owner/admin wherever it appears
 * (GrackleCard, the watch page, admin Content/Retention) — never to a plain viewer, since the
 * server only ever sends `on_legal_hold: true` to someone who may see it. */
export function HoldBadge() {
  return (
    <Badge tone="outline" size="sm" icon="flag">
      On legal hold
    </Badge>
  );
}

/** Neutral information, or an error. Status is never colour alone, so each tone carries its icon. */
export function Notice({ children, tone = "default" }: { children: ReactNode; tone?: "default" | "error" }) {
  return tone === "error" ? (
    <Callout tone="danger" icon="warning-circle">
      {children}
    </Callout>
  ) : (
    <Callout tone="info">{children}</Callout>
  );
}
